/**
 * @module ReasoningCommentGroup
 * @purpose Renders one ClusterExplanationLine with its resolved artefacts in causal-evidence-chain order.
 * @depends types/reasoning, react-i18next
 * @dependedOnBy ReasoningClusterCard
 *
 * @rules
 * - Performance: caps resolved artefact display at MAX_VISIBLE_ARTEFACTS; uses "view all" footer link
 * - State: none — all data passed as props
 * - Errors: found:false observations rendered with placeholder copy
 */

import { useTranslation } from 'react-i18next'
import type { ClusterExplanationLine, ResolvedObservation } from '../../../../types/reasoning'
import { causalTier } from '../../../../lib/assertion-display'

const MAX_VISIBLE_ARTEFACTS = 200

const ITEM_STYLE: React.CSSProperties = {
  fontSize: '0.72rem',
  color: 'var(--text)',
  padding: '2px 0',
  borderBottom: '1px solid var(--border)',
}
const CHIP_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.6rem',
  padding: '1px 5px',
  borderRadius: '4px',
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  marginRight: '4px',
  display: 'inline-block',
}

function compareObservations(left: ResolvedObservation, right: ResolvedObservation): number {
  const tierDiff = causalTier(left) - causalTier(right)
  if (tierDiff !== 0) return tierDiff
  const typeLeft = left.sourceArtefactType ?? ''
  const typeRight = right.sourceArtefactType ?? ''
  if (typeLeft !== typeRight) return typeLeft.localeCompare(typeRight)
  return (left.sourceArtefactId ?? '').localeCompare(right.sourceArtefactId ?? '')
}

function sortObservations(obs: ResolvedObservation[]): ResolvedObservation[] {
  return [...obs].sort(compareObservations)
}

interface ReasoningArtefactItemProps {
  obs: ResolvedObservation
}

function renderArtefactFound(obs: ResolvedObservation): React.ReactElement {
  const row = obs.sourceArtefactRow
  return (
    <li style={ITEM_STYLE}>
      <span style={CHIP_STYLE}>{obs.sourceArtefactType ?? '—'}</span>
      <span>{row?.displayName ?? obs.sourceArtefactId ?? '—'}</span>
      {row?.displayPath !== undefined && row.displayPath !== null && (
        <span style={{ color: 'var(--text-muted)', marginLeft: '4px' }}>{row.displayPath}</span>
      )}
      {obs.hostName !== undefined && obs.hostName !== null && (
        <span style={{ color: 'var(--text-muted)', marginLeft: '4px' }}>({obs.hostName})</span>
      )}
    </li>
  )
}

function ReasoningArtefactItem({ obs }: ReasoningArtefactItemProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  if (!obs.found) {
    return (
      <li style={{ ...ITEM_STYLE, color: 'var(--text-muted)', fontStyle: 'italic' }}>
        {t('reasoning.commentGroup.noSourceArtefact')}
      </li>
    )
  }
  return renderArtefactFound(obs)
}

interface ReasoningCommentGroupProps {
  line: ClusterExplanationLine
  resolved: Map<string, ResolvedObservation>
  onViewAllInCanvas: () => void
}

/**
 * Renders a single explanation line with its contributing resolved observations.
 *
 * @behaviour Pre-rendered engine text is displayed as-is (no client-side i18n substitution).
 *            Observations sorted in causal-evidence-chain order per FR14.
 *            Caps visible items at 200; footer link reveals remaining via Canvas drill-down.
 * @errors found:false observations render placeholder instead of artefact details.
 */
export function ReasoningCommentGroup({
  line,
  resolved,
  onViewAllInCanvas,
}: ReasoningCommentGroupProps): React.ReactElement {
  const { t } = useTranslation('analysis')

  const contributing = line.contributingObservationIds
    .map((id: string) => resolved.get(id))
    .filter((obs): obs is ResolvedObservation => obs !== undefined)

  const sorted = sortObservations(contributing)
  const visible = sorted.slice(0, MAX_VISIBLE_ARTEFACTS)
  const remaining = sorted.length - visible.length

  return (
    <div style={{ marginBottom: '12px' }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.62rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-muted)',
          marginBottom: '2px',
        }}
      >
        {line.commentType}
      </div>
      <p style={{ fontSize: '0.8rem', color: 'var(--text)', marginBottom: '6px', lineHeight: 1.45 }}>
        {line.text}
      </p>
      {visible.length > 0 && (
        <ol role="list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {visible.map((obs) => (
            <ReasoningArtefactItem key={obs.observationId} obs={obs} />
          ))}
        </ol>
      )}
      {remaining > 0 && (
        <button
          type="button"
          onClick={onViewAllInCanvas}
          className="mt-1 text-xs underline hover:no-underline"
          style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}
        >
          {t('reasoning.commentGroup.viewAllInCanvas', { count: sorted.length })}
        </button>
      )}
    </div>
  )
}
